import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Map, { Marker } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import QrCode from "../../components/QrCode";
import AuthModal from "../../components/AuthModal";
import { setBodyBg } from "../../styles/commonHooks";

// ─── Inline global styles ────────────────────────────────────────────────────
const GLOBAL_STYLE = `@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,300&family=DM+Serif+Display:ital@0;1&display=swap'); *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; } html { scroll-behavior: smooth; } .edp-root { font-family: 'DM Sans', sans-serif; } .edp-root * { -webkit-font-smoothing: antialiased; } @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } } @keyframes pulse-glow { 0%, 100% { box-shadow: 0 0 0 0 rgba(99,102,241,0.4); } 50% { box-shadow: 0 0 0 12px rgba(99,102,241,0); } } @keyframes spin { to { transform: rotate(360deg); } } @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }`;

function injectStyle(css) {
  if (typeof document === "undefined") return;
  if (document.getElementById("edp-global-style")) return;
  const el = document.createElement("style");
  el.id = "edp-global-style";
  el.textContent = css;
  document.head.appendChild(el);
}

// ─── Samsung browser detection ───────────────────────────────────────────────
function isSamsungBrowser() {
  return typeof navigator !== "undefined" && /SamsungBrowser/i.test(navigator.userAgent);
}

// ─── Theme tokens ─────────────────────────────────────────────────────────────
const LIGHT_THEME = {
  bg: "#FFF3E6",
  surface: "#FFF8F2",
  surfaceHover: "#FFF0E0",
  card: "#ffffff",
  cardHover: "#FFF0E0",
  border: "rgba(56,25,49,0.12)",
  borderStrong: "rgba(56,25,49,0.28)",
  fg: "#1a0c14",
  fgMuted: "#5a3045",
  fgSubtle: "#8a6070",
  primary: "#381931",
  primaryLight: "#6b2e58",
  accent: "#6b2e58",
  accentLight: "#8a4070",
  success: "#2a7a50",
  danger: "#c0392b",
  tagBg: "rgba(56,25,49,0.08)",
  tagColor: "#381931",
  overlayBg: "rgba(0,0,0,0.6)",
  shimmerStart: "rgba(56,25,49,0.04)",
  shimmerMid: "rgba(56,25,49,0.10)",
  btnGradient: "linear-gradient(135deg, #381931 0%, #7d3468 100%)",
  btnColor: "#FFF3E6",
  btnJoinGradient: "linear-gradient(135deg, #381931 0%, #7d3468 100%)",
  btnJoinColor: "#FFF3E6",
  priceColor: "#381931",
  stickyBg: "rgba(255,243,230,0.95)",
};

// Samsung: плоские кнопки + салатовый Join
const SAMSUNG_THEME = {
  ...LIGHT_THEME,
  btnGradient: "rgba(56,25,49,0.12)",
  btnColor: "#381931",
  btnJoinGradient: "rgba(187,247,208,0.95)",
  btnJoinColor: "#166534",
  priceColor: "#381931",
  stickyBg: "rgba(255,243,230,0.98)",
};

function getTheme(activeTheme) {
  if (activeTheme === "dark") return {
    bg: "#020617",
    surface: "#0d1424",
    surfaceHover: "#131d30",
    card: "#111827",
    cardHover: "#1a2540",
    border: "rgba(99,102,241,0.12)",
    borderStrong: "rgba(99,102,241,0.28)",
    fg: "#f1f5f9",
    fgMuted: "#94a3b8",
    fgSubtle: "#475569",
    primary: "#6366f1",
    primaryLight: "#818cf8",
    accent: "#8b5cf6",
    accentLight: "#a78bfa",
    success: "#10b981",
    danger: "#ef4444",
    tagBg: "rgba(99,102,241,0.1)",
    tagColor: "#818cf8",
    overlayBg: "rgba(2,6,23,0.85)",
    shimmerStart: "rgba(99,102,241,0.04)",
    shimmerMid: "rgba(99,102,241,0.12)",
    btnGradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    btnColor: "white",
    btnJoinGradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    btnJoinColor: "white",
    stickyBg: "rgba(13, 20, 36, 0.92)",
  };
  if (activeTheme === "samsung") return SAMSUNG_THEME;
  return LIGHT_THEME;
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function Spinner({ color }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 18,
        height: 18,
        border: `2px solid transparent`,
        borderTopColor: color || "#fff",
        borderRadius: "50%",
        animation: "spin 0.7s linear infinite",
        verticalAlign: "middle",
      }}
    />
  );
}

function Tag({ children, t }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 12px",
        background: t.tagBg,
        color: t.tagColor,
        border: `1px solid ${t.borderStrong}`,
        borderRadius: 999,
        fontSize: "0.78rem",
        fontWeight: 500,
        letterSpacing: "0.02em",
      }}
    >
      {children}
    </span>
  );
}

function MetaChip({ icon, label, accent, t, isMobile, href }) {
  const content = (
    <>
      <span style={{ fontSize: "1rem", lineHeight: 1.4, flexShrink: 0 }}>{icon}</span>
      <span style={{ wordBreak: "break-word", overflowWrap: "break-word", minWidth: 0, lineHeight: 1.4 }}>{label}</span>
    </>
  );
  const baseStyle = {
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
    padding: isMobile ? "8px 12px" : "10px 16px",
    background: t.card,
    border: `1px solid ${t.border}`,
    borderRadius: 12,
    fontSize: isMobile ? "0.82rem" : "0.88rem",
    fontWeight: 500,
    color: accent ? t.primary : t.fg,
    minWidth: 0,
    maxWidth: "100%",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    textDecoration: "none",
    transition: "border-color 0.2s ease, background 0.2s ease",
    cursor: href ? "pointer" : "default",
  };
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" style={baseStyle}
         onMouseEnter={e => { e.currentTarget.style.borderColor = t.primary; e.currentTarget.style.background = `${t.primary}11`; }}
         onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.background = t.card; }}
      >
        {content}
      </a>
    );
  }
  return <div style={baseStyle}>{content}</div>;
}

function Divider({ t }) {
  return <div style={{ width: "100%", height: 1, background: t.border, margin: "24px 0" }} />;
}

function SectionLabel({ children, t }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 20,
      }}
    >
      <span
        style={{
          display: "block",
          width: 3,
          height: 18,
          borderRadius: 99,
          background: `linear-gradient(180deg, ${t.primary}, ${t.accent})`,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize: "0.72rem",
          fontWeight: 600,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: t.fgMuted,
        }}
      >
        {children}
      </span>
    </div>
  );
}

// ─── Loading screen ───────────────────────────────────────────────────────────
function LoadingScreen({ t }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 20,
        background: t.bg,
        color: t.fgMuted,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          border: `2px solid ${t.border}`,
          borderTopColor: t.primary,
          animation: "spin 0.9s linear infinite",
        }}
      />
      <span style={{ fontSize: "0.9rem", letterSpacing: "0.06em" }}>Loading…</span>
    </div>
  );
}

// ─── Error screen ─────────────────────────────────────────────────────────────
function ErrorScreen({ message, onBack, t }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 24,
        background: t.bg,
        padding: "32px 24px",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          background: `${t.danger}15`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1.8rem",
        }}
      >
        ⚠️
      </div>
      <p style={{ color: t.fg, fontWeight: 500, textAlign: "center", maxWidth: 360 }}>
        {message || "Event not found"}
      </p>
      <button
        onClick={onBack}
        style={{
          padding: "10px 24px",
          background: t.card,
          border: `1px solid ${t.border}`,
          borderRadius: 10,
          color: t.primary,
          fontWeight: 600,
          fontSize: "0.88rem",
          cursor: "pointer",
          letterSpacing: "0.02em",
        }}
      >
        ← Back to Events
      </button>
    </div>
  );
}

// ─── Phone modal ──────────────────────────────────────────────────────────────
function PhoneModal({ lang, t, phoneInput, setPhoneInput, phoneError, setPhoneError, registrationLoading, onSubmit, onClose, isGuruDomain = false }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed",
        inset: 0,
        background: t.overlayBg,
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 24,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ type: "spring", stiffness: 380, damping: 30 }}
        style={{
          width: "100%",
          maxWidth: 440,
          background: t.card,
          border: `1px solid ${t.border}`,
          borderRadius: 20,
          padding: "36px 32px 32px",
          position: "relative",
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            width: 32,
            height: 32,
            border: `1px solid ${t.border}`,
            borderRadius: 8,
            background: "transparent",
            color: t.fgMuted,
            fontSize: "1.1rem",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
          }}
        >
          ×
        </button>

        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 14,
            background: `${t.primary}15`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.4rem",
            marginBottom: 20,
          }}
        >
          📱
        </div>

        <h3
          style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: "1.5rem",
            fontWeight: 400,
            color: t.fg,
            marginBottom: 8,
          }}
        >
          {lang === "fr" ? "Numéro requis" : "Phone Required"}
        </h3>

        <p style={{ fontSize: "0.88rem", color: t.fgMuted, lineHeight: 1.65, marginBottom: 28 }}>
          {lang === "fr"
            ? "Pour participer, veuillez fournir votre numéro de téléphone. Il ne sera visible que pour l'organisateur."
            : "To join this event, please provide your phone number. It will only be visible to the organizer."}
        </p>

        <div style={{ marginBottom: 16 }}>
          <input
            type="tel"
            value={phoneInput}
            onChange={(e) => {
              setPhoneInput(e.target.value);
              if (phoneError) setPhoneError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && onSubmit()}
            placeholder={isGuruDomain ? "+91 98765 43210" : (lang === "fr" ? "+33 6 12 34 56 78" : "+1 234 567 890")}
            autoFocus
            style={{
              width: "100%",
              padding: "14px 16px",
              border: `1.5px solid ${phoneError ? t.danger : t.border}`,
              borderRadius: 12,
              background: t.surface,
              color: t.fg,
              fontSize: "1rem",
              fontFamily: "'DM Sans', sans-serif",
              outline: "none",
              transition: "border-color 0.2s",
            }}
          />
          {phoneError && (
            <p style={{ color: t.danger, fontSize: "0.8rem", marginTop: 6 }}>{phoneError}</p>
          )}
        </div>

        <button
          onClick={onSubmit}
          disabled={registrationLoading || !phoneInput.trim()}
          style={{
            width: "100%",
            padding: "14px",
            background: registrationLoading
              ? t.fgSubtle
              : t.btnGradient,
            color: registrationLoading ? "white" : t.btnColor,
            border: "none",
            borderRadius: 12,
            fontWeight: 600,
            fontSize: "0.95rem",
            cursor: registrationLoading || !phoneInput.trim() ? "not-allowed" : "pointer",
            opacity: !phoneInput.trim() ? 0.55 : 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            transition: "opacity 0.2s",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          {registrationLoading && <Spinner />}
          {registrationLoading
            ? lang === "fr" ? "Enregistrement…" : "Registering…"
            : lang === "fr" ? "Confirmer et rejoindre" : "Confirm & Join"}
        </button>

        <p
          style={{
            marginTop: 16,
            textAlign: "center",
            fontSize: "0.76rem",
            color: t.fgSubtle,
            lineHeight: 1.5,
          }}
        >
          🔒{" "}
          {lang === "fr"
            ? "Chiffré et partagé uniquement avec l'organisateur"
            : "Encrypted and shared only with the event organizer"}
        </p>
      </motion.div>
    </motion.div>
  );
}

// ─── CommentItem ─────────────────────────────────────────────────────────────
function CommentItem({ comment, t, token, onReply, onDelete, replyingTo, replyBody, setReplyBody, replyPosting, postReply, setReplyingTo, currentUserId, lang, depth = 0 }) {
  const isOwn = currentUserId && comment.author.id === currentUserId;
  const isReplyingHere = replyingTo?.id === comment.id;
  const avatarLetter = (comment.author.name || "?")[0].toUpperCase();

  const timeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr)) / 1000;
    if (diff < 60) return lang === "fr" ? "à l'instant" : "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}${lang === "fr" ? " min" : "m"}`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}${lang === "fr" ? " h" : "h"}`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}${lang === "fr" ? " j" : "d"}`;
    return new Date(dateStr).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { month: "short", day: "numeric" });
  };

  return (
    <div style={{ marginLeft: depth > 0 ? 44 : 0, marginTop: depth > 0 ? 12 : 0 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        {/* Avatar */}
        <div style={{
          flexShrink: 0,
          width: depth > 0 ? 30 : 36,
          height: depth > 0 ? 30 : 36,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${t.primary}, ${t.accent})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: depth > 0 ? "0.7rem" : "0.82rem",
          fontWeight: 700, color: "white", userSelect: "none",
          overflow: "hidden",
        }}>
          {comment.author?.avatar_url ? (
            <img
              src={comment.author.avatar_url}
              alt={comment.author.name}
              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.parentElement.innerHTML = `<span style="font-size:${depth > 0 ? '0.7rem' : '0.82rem'};font-weight:700;color:white">${avatarLetter}</span>`;
              }}
            />
          ) : (
            avatarLetter
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Author + timestamp */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: "0.85rem", fontWeight: 600, color: t.fg }}>
              {comment.author.name}
            </span>
            <span style={{ fontSize: "0.75rem", color: t.fgSubtle }}>{timeAgo(comment.created_at)}</span>
          </div>

          {/* Body */}
          <p style={{
            fontSize: "0.9rem", lineHeight: 1.65, color: comment.is_deleted ? t.fgSubtle : t.fgMuted,
            fontStyle: comment.is_deleted ? "italic" : "normal",
            wordBreak: "break-word", margin: 0,
          }}>
            {comment.body}
          </p>

          {/* Actions */}
          {!comment.is_deleted && (
            <div style={{ display: "flex", gap: 14, marginTop: 8 }}>
              {token && (
                <button
                  onClick={() => onReply(comment)}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: "0.78rem", fontWeight: 600, color: t.fgSubtle,
                    padding: 0, fontFamily: "inherit",
                    transition: "color 0.15s",
                  }}
                  onMouseEnter={e => e.target.style.color = t.primary}
                  onMouseLeave={e => e.target.style.color = t.fgSubtle}
                >
                  {lang === "fr" ? "↩ Répondre" : "↩ Reply"}
                </button>
              )}
              {isOwn && (
                <button
                  onClick={() => onDelete(comment.id)}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: "0.78rem", fontWeight: 600, color: t.fgSubtle,
                    padding: 0, fontFamily: "inherit",
                    transition: "color 0.15s",
                  }}
                  onMouseEnter={e => e.target.style.color = t.danger}
                  onMouseLeave={e => e.target.style.color = t.fgSubtle}
                >
                  {lang === "fr" ? "Supprimer" : "Delete"}
                </button>
              )}
            </div>
          )}

          {/* Inline reply box */}
          {isReplyingHere && (
            <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "flex-start" }}>
              <textarea
                autoFocus
                placeholder={lang === "fr" ? `Répondre à ${comment.author.name}…` : `Reply to ${comment.author.name}…`}
                value={replyBody}
                onChange={e => setReplyBody(e.target.value)}
                rows={2}
                style={{
                  flex: 1, resize: "vertical",
                  padding: "10px 14px",
                  background: t.surface,
                  border: `1.5px solid ${t.border}`,
                  borderRadius: 12,
                  color: t.fg, fontSize: "0.88rem", fontFamily: "inherit",
                  outline: "none",
                  transition: "border-color 0.2s",
                  minHeight: 56,
                }}
                onFocus={e => e.target.style.borderColor = t.primary}
                onBlur={e => e.target.style.borderColor = t.border}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); postReply(); }
                  if (e.key === "Escape") setReplyingTo(null);
                }}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <button
                  onClick={postReply}
                  disabled={replyPosting || !replyBody.trim()}
                  style={{
                    padding: "8px 14px", borderRadius: 10, border: "none", cursor: "pointer",
                    background: replyPosting || !replyBody.trim() ? t.fgSubtle : `linear-gradient(135deg, ${t.primary}, ${t.accent})`,
                    color: "white", fontSize: "0.8rem", fontWeight: 600, fontFamily: "inherit",
                    opacity: !replyBody.trim() ? 0.5 : 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {replyPosting ? "…" : (lang === "fr" ? "Envoyer" : "Send")}
                </button>
                <button
                  onClick={() => { setReplyingTo(null); setReplyBody(""); }}
                  style={{
                    padding: "8px 14px", borderRadius: 10,
                    border: `1px solid ${t.border}`,
                    background: "transparent", cursor: "pointer",
                    color: t.fgMuted, fontSize: "0.8rem", fontFamily: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  {lang === "fr" ? "Annuler" : "Cancel"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Nested replies */}
      {comment.replies?.length > 0 && (
        <div style={{
          marginLeft: 44, marginTop: 4,
          borderLeft: `2px solid ${t.border}`,
          paddingLeft: 16,
        }}>
          {comment.replies.map(reply => (
            <CommentItem
              key={reply.id}
              comment={reply}
              t={t} token={token}
              onReply={onReply} onDelete={onDelete}
              replyingTo={replyingTo} replyBody={replyBody}
              setReplyBody={setReplyBody} replyPosting={replyPosting}
              postReply={postReply} setReplyingTo={setReplyingTo}
              currentUserId={currentUserId} lang={lang}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── CommentsSection ──────────────────────────────────────────────────────────
function CommentsSection({
  t, lang, token, isMobile,
  comments, commentsLoading,
  commentBody, setCommentBody, commentPosting, postComment,
  replyingTo, setReplyingTo, replyBody, setReplyBody, replyPosting, postReply,
  deleteComment, currentUserId,
  onNeedAuth,
}) {
  const total = comments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.5 }}
      style={{
        background: t.card,
        border: `1px solid ${t.border}`,
        borderRadius: isMobile ? 16 : 20,
        padding: isMobile ? "24px 20px" : "32px 36px",
        marginTop: isMobile ? 16 : 20,
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
        <span style={{ display: "block", width: 3, height: 18, borderRadius: 99, background: `linear-gradient(180deg, ${t.primary}, ${t.accent})`, flexShrink: 0 }} />
        <span style={{ fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: t.fgMuted }}>
          {lang === "fr" ? `Avis & Commentaires` : `Reviews & Comments`}
        </span>
        {total > 0 && (
          <span style={{
            marginLeft: "auto",
            fontSize: "0.78rem", fontWeight: 600,
            padding: "3px 10px",
            background: t.tagBg, color: t.tagColor,
            border: `1px solid ${t.borderStrong}`,
            borderRadius: 999,
          }}>
            {total}
          </span>
        )}
      </div>

      {/* Composer */}
      {token ? (
        <div style={{ marginBottom: 28 }}>
          <textarea
            placeholder={lang === "fr" ? "Partagez votre avis sur cet événement…" : "Share your thoughts about this event…"}
            value={commentBody}
            onChange={e => setCommentBody(e.target.value)}
            rows={3}
            style={{
              width: "100%", resize: "vertical",
              padding: "14px 16px",
              background: t.surface,
              border: `1.5px solid ${t.border}`,
              borderRadius: 14,
              color: t.fg, fontSize: "0.92rem", fontFamily: "inherit",
              outline: "none",
              transition: "border-color 0.2s",
              minHeight: 72,
              boxSizing: "border-box",
            }}
            onFocus={e => e.target.style.borderColor = t.primary}
            onBlur={e => e.target.style.borderColor = t.border}
            onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) postComment(); }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
            <button
              onClick={postComment}
              disabled={commentPosting || !commentBody.trim()}
              style={{
                padding: "11px 24px", borderRadius: 12, border: "none", cursor: "pointer",
                background: commentPosting || !commentBody.trim() ? t.fgSubtle : `linear-gradient(135deg, ${t.primary}, ${t.accent})`,
                color: "white", fontSize: "0.88rem", fontWeight: 600, fontFamily: "inherit",
                opacity: !commentBody.trim() ? 0.5 : 1,
                boxShadow: commentBody.trim() ? `0 4px 16px ${t.primary}44` : "none",
                transition: "opacity 0.2s, box-shadow 0.2s",
              }}
            >
              {commentPosting ? "…" : (lang === "fr" ? "Publier" : "Post comment")}
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={onNeedAuth}
          style={{
            marginBottom: 28,
            padding: "18px 20px",
            border: `1.5px dashed ${t.borderStrong}`,
            borderRadius: 14,
            textAlign: "center",
            cursor: "pointer",
            color: t.fgMuted, fontSize: "0.88rem",
            transition: "background 0.2s",
          }}
          onMouseEnter={e => e.currentTarget.style.background = `${t.primary}08`}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <span style={{ color: t.primary, fontWeight: 600 }}>
            {lang === "fr" ? "Connectez-vous" : "Sign in"}
          </span>
          {" "}
          {lang === "fr" ? "pour laisser un avis" : "to leave a review"}
        </div>
      )}

      {/* Comment list */}
      {commentsLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}>
          <div style={{ width: 28, height: 28, border: `2px solid ${t.border}`, borderTopColor: t.primary, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        </div>
      ) : comments.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 0", color: t.fgSubtle, fontSize: "0.88rem" }}>
          <div style={{ fontSize: "2rem", marginBottom: 10, opacity: 0.4 }}>💬</div>
          {lang === "fr" ? "Soyez le premier à laisser un avis !" : "Be the first to leave a review!"}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {comments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              t={t} token={token}
              onReply={c => { setReplyingTo(c); setReplyBody(""); }}
              onDelete={deleteComment}
              replyingTo={replyingTo} replyBody={replyBody}
              setReplyBody={setReplyBody} replyPosting={replyPosting}
              postReply={postReply} setReplyingTo={setReplyingTo}
              currentUserId={currentUserId} lang={lang}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function EventDetailPage({ lang = "en", theme = "dark", token, onLogin, isGuruDomain = false, onRegisterAuthModal }) {
  injectStyle(GLOBAL_STYLE);
  const { slug } = useParams();
  const navigate = useNavigate();

  // ── Core state ──────────────────────────────────────────────────────────────
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  // Регистрируем функцию открытия модалки для MobileSiteHeader / SiteHeader.
  // Хедер вызывает onRegisterAuthModal(fn) при маунте и onRegisterAuthModal(null) при анмаунте.
  useEffect(() => {
    if (!onRegisterAuthModal) return;
    onRegisterAuthModal(() => setIsAuthModalOpen(true));
    return () => onRegisterAuthModal(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRegisterAuthModal]);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isNarrow, setIsNarrow] = useState(window.innerWidth > 768 && window.innerWidth <= 1400);

  // ── Referral state ──────────────────────────────────────────────────────────
  // referral содержит: { id, ref_code, referral_url, scan_count, unique_scan_count, registration_count, ... }
  const [referral, setReferral] = useState(null);

  // ── Comments state ───────────────────────────────────────────────────────────
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentBody, setCommentBody] = useState("");
  const [commentPosting, setCommentPosting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // { id, author_name }
  const [replyBody, setReplyBody] = useState("");
  const [replyPosting, setReplyPosting] = useState(false);

  const activeTheme = isSamsungBrowser() ? "samsung" : theme;
  const isDark = activeTheme === "dark";
  const t = getTheme(activeTheme);

  useEffect(() => {
    setBodyBg(isDark ? "#020617" : "#FFF3E6");
  }, [isDark]);

  // ── Parse user_id from JWT token ─────────────────────────────────────────────
  const currentUserId = useMemo(() => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.user_id || payload.sub || null;
    } catch {
      return null;
    }
  }, [token]);

  // ── Resize listener ─────────────────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w <= 768);
      setIsNarrow(w > 768 && w <= 1400);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Scroll to top on slug change ────────────────────────────────────────────
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, [slug]);

  // ── Auto-open auth modal for unauthenticated visitors ─────────────────────────
  // Срабатывает один раз после загрузки ивента, если пользователь не авторизован.
  // ref-код (?ref=XXXXX) уже сохранён в sessionStorage выше и будет
  // использован при конверсии после успешного логина / регистрации.
  const hasAutoOpenedAuth = useRef(false);
  useEffect(() => {
    if (!loading && !token && !hasAutoOpenedAuth.current) {
      hasAutoOpenedAuth.current = true;
      setIsAuthModalOpen(true);
    }
  }, [loading, token]);

  // ── Referral tracking: срабатывает если в URL есть ?ref=XXXXX ───────────────
  // Сохраняем ref_code сразу — он понадобится после регистрации на ивент.
  // Трекинг отправляем тихо — 404 не показываем в консоли (реферал мог не существовать).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref_code = params.get("ref");
    if (!ref_code) return;

    sessionStorage.setItem("pending_ref_code", ref_code);

    fetch("/api/referral/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ref_code }),
    }).then((res) => {
      if (!res.ok && res.status !== 404) {
        console.warn("Referral tracking: unexpected status", res.status);
      }
    }).catch(() => {});
  }, []);

  // ── Fetch event ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        let res = await fetch(`/api/published_events/by_slug/${slug}`, { headers });
        if (!res.ok && !isNaN(slug)) {
          // Фоллбэк для старых числовых URL (обратная совместимость)
          res = await fetch(`/api/published_events/${slug}`, { headers });
        }
        if (!res.ok) throw new Error(res.status === 404 ? "Event not found or not published" : "Failed to load event");
        setEvent(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [slug, token]);

  // ── Fetch registration status ────────────────────────────────────────────────
  // ВАЖНО: зависим от event?.id, а не от slug.
  // Это гарантирует что запрос уходит только после того как fetchEvent
  // отработал и положил event в стейт. При обновлении страницы slug и token
  // известны сразу, а event.id — только после загрузки ивента.
  const loadRegistration = useCallback(async (eventId) => {
    if (!token || !eventId) return;
    try {
      const res = await fetch(`/api/events/${eventId}/registration`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 404) { setRegistration(null); return; }
      if (!res.ok) throw new Error("Failed to load registration");
      setRegistration(await res.json());
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  useEffect(() => {
    if (!token || !event?.id) return;
    loadRegistration(event.id);
  }, [event?.id, token, loadRegistration]); // ← ждём event.id, не slug

  // ── Fetch referral QR для участника ─────────────────────────────────────────
  // Загружаем реферальный QR только когда:
  // 1. Ивент загружен
  // 2. Пользователь авторизован
  // 3. Пользователь является участником (registration есть) ИЛИ создателем ивента
  useEffect(() => {
    if (!event || !token) return;

    const isParticipant = !!registration;
    const isCreator = event.is_mine;

    if (!isParticipant && !isCreator) return;

    fetch(`/api/events/${event.id}/my-referral`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error("Referral not available");
        return r.json();
      })
      .then((data) => setReferral(data))
      .catch((err) => console.warn("Referral QR not available (non-critical):", err));
  }, [event, token, registration]);

  // ── Fetch comments ───────────────────────────────────────────────────────────
  const loadComments = async (eventId) => {
    if (!eventId) return;
    setCommentsLoading(true);
    try {
      const res = await fetch(`/api/events/${eventId}/comments`);
      if (res.ok) setComments(await res.json());
    } catch (err) {
      console.warn("Could not load comments:", err);
    } finally {
      setCommentsLoading(false);
    }
  };

  useEffect(() => {
    if (event?.id) loadComments(event.id);
  }, [event?.id]);

  // ── Post top-level comment ────────────────────────────────────────────────
  const postComment = async () => {
    if (!commentBody.trim() || !token || !event?.id) return;
    setCommentPosting(true);
    try {
      const res = await fetch(`/api/events/${event.id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ body: commentBody.trim() }),
      });
      if (!res.ok) throw new Error("Failed to post comment");
      setCommentBody("");
      await loadComments(event.id);
    } catch (err) {
      console.error(err);
    } finally {
      setCommentPosting(false);
    }
  };

  // ── Post reply ────────────────────────────────────────────────────────────
  const postReply = async () => {
    if (!replyBody.trim() || !replyingTo || !token || !event?.id) return;
    setReplyPosting(true);
    try {
      const res = await fetch(`/api/events/${event.id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ body: replyBody.trim(), parent_id: replyingTo.id }),
      });
      if (!res.ok) throw new Error("Failed to post reply");
      setReplyBody("");
      setReplyingTo(null);
      await loadComments(event.id);
    } catch (err) {
      console.error(err);
    } finally {
      setReplyPosting(false);
    }
  };

  // ── Delete comment ────────────────────────────────────────────────────────
  const deleteComment = async (commentId) => {
    if (!token || !event?.id) return;
    try {
      const res = await fetch(`/api/events/${event.id}/comments/${commentId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) await loadComments(event.id);
    } catch (err) {
      console.error(err);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const validatePhone = (phone) => {
    const digits = phone.replace(/[^\d]/g, "");
    if (isGuruDomain) {
      // Индийский номер: 10 цифр, начинается с 6-9, или с кодом +91 — 12 цифр
      const isValid = /^(\+91)?[6-9]\d{9}$/.test(phone.replace(/\s/g, ""));
      if (!isValid) return "Invalid format. Example: +91 98765 43210";
      return null;
    }
    if (digits.length < 7 || digits.length > 15) {
      return lang === "fr" ? "Format invalide. Exemple: +33612345678" : "Invalid format. Example: +1234567890";
    }
    return null;
  };

  // ── Конверсия реферала: вызывается после успешной регистрации на ивент ────────
  // activeToken передаётся явно — props.token может быть ещё не обновлён
  // в момент вызова сразу после логина через AuthModal
  const trackReferralConversion = async (activeToken) => {
    const ref_code = sessionStorage.getItem("pending_ref_code");
    const tokenToUse = activeToken || token;
    if (!ref_code || !tokenToUse) return;

    try {
      const res = await fetch("/api/referral/register-conversion", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenToUse}`,
        },
        body: JSON.stringify({ ref_code }),
      });
      if (res.ok) {
        sessionStorage.removeItem("pending_ref_code");
      }
    } catch (err) {
      console.warn("Conversion tracking failed (non-critical):", err);
    }
  };

  // ── Join handlers ────────────────────────────────────────────────────────────
  const joinEventWithToken = async (tokenToUse) => {
    try {
      setRegistrationLoading(true);
      const res = await fetch(`/api/events/${event.id}/register`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenToUse}` },
      });
      if (!res.ok) throw new Error("Failed to register");
      const data = await res.json();
      setRegistration(data);
      // Передаём tokenToUse явно — props.token может быть ещё не обновлён
      await trackReferralConversion(tokenToUse);
    } catch (err) {
      console.error(err);
      setError(lang === "fr" ? "Impossible de rejoindre l'événement" : "Failed to join event");
    } finally {
      setRegistrationLoading(false);
    }
  };

  const joinEventWithPhone = async () => {
    const err = validatePhone(phoneInput);
    if (err) { setPhoneError(err); return; }
    setRegistrationLoading(true);
    setShowPhoneModal(false);
    try {
      const res = await fetch(`/api/events/${event.id}/register`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ phone_number: phoneInput.trim() }),
      });
      if (!res.ok) {
        const errData = await res.json();
        if (errData.detail?.error === "phone_required") {
          setPhoneError(lang === "fr" ? "Numéro de téléphone requis" : "Phone number required");
          setShowPhoneModal(true);
        } else {
          throw new Error(errData.detail?.message || "Failed to register");
        }
        return;
      }
      const data = await res.json();
      setRegistration(data);
      setPhoneInput("");
      setPhoneError("");
      await trackReferralConversion(token);
    } catch (err) {
      console.error("Registration error: ", err);
      setPhoneError(err.message || (lang === "fr" ? "Erreur d'inscription" : "Registration error"));
      setShowPhoneModal(true);
    } finally {
      setRegistrationLoading(false);
    }
  };

  const joinEvent = async () => {
    if (!token) { setIsAuthModalOpen(true); return; }
    if (!event || loading) {
      setError(lang === "fr" ? "Événement en chargement…" : "Event loading, please wait…");
      return;
    }
    if (event.require_participant_phone) { setShowPhoneModal(true); return; }
    await joinEventWithToken(token);
  };

  const handleAuthSuccess = async (newToken) => {
    setIsAuthModalOpen(false);
    if (onLogin) onLogin(newToken);
    if (!event?.id) return;

    try {
      const res = await fetch(`/api/events/${event.id}/registration`, {
        headers: { Authorization: `Bearer ${newToken}` },
      });
      if (res.ok) {
        // Регистрация на ивент уже есть (бэкенд авто-джойнил через EVENT- код).
        // Обновляем стейт и трекаем реферальную конверсию — юзер только что зарегался.
        const regData = await res.json();
        setRegistration(regData);
        await trackReferralConversion(newToken);
      } else if (res.status === 404) {
        // Аккаунт создан без джоина на ивент (личный инвайт-код).
        // Юзер сам нажмёт Join — trackReferralConversion вызовется в joinEventWithToken.
        if (event.require_participant_phone) { setShowPhoneModal(true); return; }
        await joinEventWithToken(newToken);
      }
    } catch (err) {
      setError(err.message || (lang === "fr" ? "Erreur" : "Error"));
    }
  };

  // ── Guard states ─────────────────────────────────────────────────────────────
  if (loading) return <LoadingScreen t={t} />;
  if (error || !event) return <ErrorScreen message={error} onBack={() => navigate("/events")} t={t} />;

  // ── Derived values ────────────────────────────────────────────────────────────
  const eventUrl = event.event_url || `${window.location.origin}/events/${event.slug || event.id}`;
  const priceText = !event.ticket_price || event.ticket_price === 0
    ? lang === "fr" ? "Gratuit" : "Free"
    : `${event.ticket_price} ${event.currency || "€"}`;
  const isFree = !event.ticket_price || event.ticket_price === 0;
  const dateStr = event.event_date?.split("T")[0] || "";
  const timeStr = event.event_time ? event.event_time.slice(0, 5) : "";
  const btnState = registrationLoading ? "loading"
    : registration ? "joined"
    : !token ? "login"
    : "join";
  const joinBtnLabel = {
    loading: lang === "fr" ? "Chargement…" : "Loading…",
    joined: lang === "fr" ? "✓ Vous participez" : "✓ You're in",
    login: lang === "fr" ? "Se connecter pour participer" : "Sign in to join",
    join: lang === "fr" ? "Rejoindre l'événement" : "Join Event",
  }[btnState];
  const joinBtnBg = {
    loading: t.fgSubtle,
    joined: `linear-gradient(135deg, ${t.success}cc, ${t.success})`,
    login: `linear-gradient(135deg, #475569, #64748b)`,
    join: t.btnJoinGradient,
  }[btnState];
  const joinBtnColor = btnState === "join" ? t.btnJoinColor : "white";

  // Определяем что показывать в левой колонке (QR)
  // Приоритет: персональный реферальный QR (если участник) > обычный QR ивента
  const showQrColumn = event.allow_user_invitations || event.is_mine;
  const qrUrl = referral ? referral.referral_url : eventUrl;
  const qrStats = referral
    ? {
        scan_count: referral.scan_count,
        unique_scan_count: referral.unique_scan_count,
        registration_count: referral.registration_count,
      }
    : null;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Background decoration (identical to EventsPage) ── */}
      <div aria-hidden="true" style={{
        position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", top: "-10%", left: "-8%",
          width: "520px", height: "520px", borderRadius: "50%",
          background: isDark
            ? "radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(107,46,88,0.12) 0%, transparent 70%)",
          filter: "blur(50px)",
        }} />
        <div style={{
          position: "absolute", bottom: "-12%", right: "-6%",
          width: "600px", height: "600px", borderRadius: "50%",
          background: isDark
            ? "radial-gradient(circle, rgba(139,92,246,0.14) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(56,25,49,0.08) 0%, transparent 70%)",
          filter: "blur(60px)",
        }} />
        <div style={{
          position: "absolute", top: "40%", left: "30%",
          width: "700px", height: "400px", borderRadius: "50%",
          background: isDark
            ? "radial-gradient(circle, rgba(124,58,237,0.07) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(107,46,88,0.05) 0%, transparent 70%)",
          filter: "blur(60px)",
        }} />
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: isDark
            ? "linear-gradient(rgba(99,102,241,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.04) 1px, transparent 1px)"
            : "linear-gradient(rgba(56,25,49,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,25,49,0.04) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }} />
      </div>
      <div className="edp-root" style={{ position: "relative", zIndex: 1, minHeight: "100vh", background: "transparent", color: t.fg }}>
        <div style={{ position: "relative", zIndex: 2, minHeight: "100vh", width: "100%" }}>
          {/* ── Content container ── */}
          <div
            style={{
              width: "100%",
              minHeight: "100vh",
              background: isDark ? "rgba(2,6,23,0.75)" : "rgba(255,243,230,0.75)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              borderLeft: isMobile ? "none" : `1px solid ${t.border}`,
              borderRight: isMobile ? "none" : `1px solid ${t.border}`,
              padding: isMobile ? "12px 12px 80px" : "20px 32px 120px",
              minWidth: 0,
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            {/* ── Cover hero (inside container) ── */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
              style={{
                position: "relative",
                width: "100%",
                height: isMobile ? 180 : 340,
                borderRadius: isMobile ? 16 : 20,
                overflow: "hidden",
                marginBottom: isMobile ? 0 : 28,
              }}
            >
              {event.event_cover_url ? (
                <>
                  {/* Background: stretched cover + blur + 50% darken */}
                  <img
                    src={event.event_cover_url}
                    alt=""
                    aria-hidden
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                      filter: "blur(22px)",
                      transform: "scale(1.12)",
                      transformOrigin: "center",
                    }}
                  />
                  <div
                    aria-hidden
                    style={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 1,
                      background: "rgba(0,0,0,0.5)",
                      pointerEvents: "none",
                    }}
                  />
                  {/* Foreground: same image at natural aspect ratio, centered */}
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                      padding: isMobile ? 8 : 14,
                    }}
                  >
                    <motion.img
                      src={event.event_cover_url}
                      alt={event.title}
                      initial={{ scale: 1.04, opacity: 0.9 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 1.05, ease: "easeOut" }}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  </div>
                </>
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    background: `linear-gradient(135deg, ${t.primary}dd 0%, ${t.accent}cc 60%, #020617 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'DM Serif Display', serif",
                      fontSize: isMobile ? "1.8rem" : "3rem",
                      fontWeight: 400,
                      color: "white",
                      textAlign: "center",
                      padding: "0 32px",
                      lineHeight: 1.2,
                      letterSpacing: "-0.01em",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      maxWidth: "100%",
                    }}
                  >
                    {event.title}
                  </span>
                </div>
              )}
              {/* subtle bottom fade */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: isMobile
                    ? `linear-gradient(to bottom, transparent 40%, ${t.bg}cc 80%, ${t.bg} 100%)`
                    : `linear-gradient(to bottom, transparent 55%, ${t.bg}88 100%)`,
                  pointerEvents: "none",
                }}
              />
              {/* ── Back button overlaid on cover — both mobile and desktop ── */}
              <motion.button
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.35 }}
                onClick={() => navigate("/events")}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(0,0,0,0.62)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.32)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(0,0,0,0.38)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)";
                }}
                style={{
                  position: "absolute",
                  top: isMobile ? 10 : 16,
                  left: isMobile ? 10 : 16,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: isMobile ? 5 : 7,
                  padding: isMobile ? "6px 12px" : "9px 18px",
                  background: "rgba(0,0,0,0.38)",
                  backdropFilter: "blur(14px)",
                  WebkitBackdropFilter: "blur(14px)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  borderRadius: 999,
                  color: "#fff",
                  fontSize: isMobile ? "0.78rem" : "0.85rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  zIndex: 10,
                  letterSpacing: "0.01em",
                  boxShadow: "0 2px 16px rgba(0,0,0,0.35), 0 1px 3px rgba(0,0,0,0.25)",
                  transition: "background 0.2s, border-color 0.2s",
                  userSelect: "none",
                }}
              >
                <svg width={isMobile ? 13 : 15} height={isMobile ? 13 : 15} viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0, opacity: 0.9 }}>
                  <path d="M9.5 3L5 7.5L9.5 12" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {lang === "fr" ? "Retour" : "Back"}
              </motion.button>
            </motion.div>

            {/* ── MOBILE ONLY: sticky QR + Join block ── */}
            {isMobile && (
              <div
                style={{
                  position: "sticky",
                  top: 54,
                  zIndex: 90,
                  marginTop: -52,
                  marginBottom: 10,
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.4 }}
                  style={{
                    background: t.stickyBg,
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                    border: `1px solid ${t.border}`,
                    borderRadius: 18,
                    padding: "10px 12px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 8,
                    boxShadow: isDark
                      ? "0 8px 32px rgba(0,0,0,0.45)"
                      : "0 8px 32px rgba(0,0,0,0.12)",
                  }}
                >
                  {/* QR code — only if allowed */}
                  {showQrColumn && (
                    <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                      <QrCode
                        url={qrUrl}
                        stats={qrStats}
                        size={150}
                        lang={lang}
                        theme={theme}
                        compact
                      />
                    </div>
                  )}

                  {/* Join button */}
                  <motion.button
                    onClick={joinEvent}
                    disabled={registrationLoading || !!registration || !event || loading}
                    whileHover={btnState === "join" ? { scale: 1.02 } : {}}
                    whileTap={btnState === "join" ? { scale: 0.97 } : {}}
                    style={{
                      width: "100%",
                      padding: "14px",
                      background: joinBtnBg,
                      color: joinBtnColor,
                      border: "none",
                      borderRadius: 14,
                      fontWeight: 600,
                      fontSize: "0.97rem",
                      cursor: registrationLoading || !!registration ? "default" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      fontFamily: "'DM Sans', sans-serif",
                      letterSpacing: "0.01em",
                      boxShadow: btnState === "join"
                        ? "0 4px 20px rgba(99,102,241,0.38)"
                        : "none",
                      transition: "box-shadow 0.2s",
                    }}
                  >
                    {registrationLoading && <Spinner />}
                    {joinBtnLabel}
                  </motion.button>

                  {/* Price / info line — показываем только цену, без дублирующего badge */}
                  <div
                    style={{
                      width: "100%",
                      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(99,102,241,0.06)",
                      border: `1px solid ${t.border}`,
                      borderRadius: 12,
                      padding: "10px 14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <span style={{ fontSize: "0.8rem", color: t.fgMuted, fontWeight: 500 }}>
                      {lang === "fr" ? "Prix" : "Price"}
                    </span>
                    <span
                      style={{
                        fontFamily: "'DM Serif Display', serif",
                        fontSize: "1.25rem",
                        fontWeight: 400,
                        ...(t.priceColor
                          ? { color: t.priceColor }
                          : {
                              background: `linear-gradient(135deg, ${t.primary}, ${t.accent})`,
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                            }),
                        lineHeight: 1,
                      }}
                    >
                      {priceText}
                    </span>
                  </div>
                </motion.div>
              </div>
            )}

            {/* ── Event header card ── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              style={{
                marginBottom: isMobile ? 12 : 32,
                background: t.card,
                border: `1px solid ${t.border}`,
                borderRadius: isMobile ? 16 : 20,
                padding: isMobile ? "16px 16px" : "32px 36px",
                position: "relative",
                minWidth: 0,
                overflow: "hidden",
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {/* Title + meta */}
              <h1
                style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontSize: isMobile ? "1.3rem" : "2.6rem",
                  fontWeight: 400,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.15,
                  marginBottom: isMobile ? 4 : 8,
                  color: t.fg,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  maxWidth: "100%",
                }}
              >
                {event.title}
              </h1>
              {event.organizer_name && (
                <p style={{ fontSize: "0.88rem", color: t.fgMuted, marginBottom: isMobile ? 10 : 20 }}>
                  {lang === "fr" ? "Organisé par" : "Hosted by"}{" "}
                  <strong style={{ color: t.fg }}>{event.organizer_name}</strong>
                </p>
              )}

              {/* Meta chips */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {event.location && (
                  <MetaChip
                    icon="📍"
                    label={event.location}
                    t={t}
                    isMobile={isMobile}
                    href={
                      event.latitude && event.longitude
                        ? `https://maps.google.com/?q=${event.latitude},${event.longitude}`
                        : `https://maps.google.com/?q=${encodeURIComponent(event.location)}`
                    }
                  />
                )}
                {dateStr && <MetaChip icon="📅" label={dateStr} t={t} isMobile={isMobile} />}
                {timeStr && <MetaChip icon="🕒" label={timeStr} t={t} isMobile={isMobile} />}
                <MetaChip icon={isFree ? "🎟" : "💳"} label={priceText} accent t={t} isMobile={isMobile} />
              </div>

              {event.require_participant_phone && (
                <div
                  style={{
                    marginTop: 12,
                    display: "inline-flex",
                    alignItems: "flex-start",
                    gap: 8,
                    padding: "8px 14px",
                    background: `${t.primary}12`,
                    border: `1px solid ${t.borderStrong}`,
                    borderRadius: 10,
                    fontSize: "0.82rem",
                    color: t.primary,
                    fontWeight: 500,
                    maxWidth: "100%",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  📱 {lang === "fr" ? "Numéro de téléphone requis" : "Phone number required to join"}
                </div>
              )}

              {!event.allow_user_invitations && !event.is_mine && (
                <div
                  style={{
                    marginTop: 16,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "10px 16px",
                    background: `${t.primary}08`,
                    border: `1px solid ${t.border}`,
                    borderRadius: 10,
                    fontSize: "0.82rem",
                    color: t.fgMuted,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  <span style={{ flexShrink: 0 }}>🔒</span>
                  <span>{lang === "fr" ? "Les invitations sont gérées par l'organisateur uniquement" : "Invitations are managed by the organizer only"}</span>
                </div>
              )}

              {event.tags?.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 20 }}>
                  {event.tags.map((tag) => (
                    <Tag key={tag} t={t}>#{tag}</Tag>
                  ))}
                </div>
              )}
            </motion.div>

            {/* ── Layout: QR | About | Map+Join ── */}
            {/* Desktop (>1100px): 3-col grid  |  Tablet (769-1100px): About full-width top, then QR+Map row  |  Mobile: stack */}

            {/* TABLET & MOBILE: About block first, full-width */}
            {(isMobile || isNarrow) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                style={{
                  background: t.card,
                  border: `1px solid ${t.border}`,
                  borderRadius: isMobile ? 16 : 20,
                  padding: isMobile ? "24px 20px" : "28px 32px",
                  marginBottom: isMobile ? 16 : 20,
                  minWidth: 0,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                <SectionLabel t={t}>{lang === "fr" ? "À propos" : "About the event"}</SectionLabel>
                <p style={{
                  fontSize: "0.93rem", lineHeight: 1.8, color: t.fgMuted,
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                  overflowWrap: "break-word", minWidth: 0, maxWidth: "100%",
                  textAlign: "center",
                }}>
                  {event.description || (lang === "fr" ? "Pas de description." : "No description provided.")}
                </p>
              </motion.div>
            )}

            <div
              style={{
                display: isMobile ? "flex" : "grid",
                flexDirection: isMobile ? "column" : undefined,
                gridTemplateColumns: isNarrow
                  ? (showQrColumn ? "minmax(0,1fr) minmax(0,1fr)" : "minmax(0,1fr)")
                  : isMobile ? undefined
                  : (showQrColumn ? "minmax(0,240px) minmax(0,1fr) minmax(0,280px)" : "minmax(0,1fr) minmax(0,280px)"),
                gap: isMobile ? 16 : 20,
                alignItems: "start",
                minWidth: 0,
              }}
            >
              {/* ── Left: QR column ── (desktop/tablet only — mobile shows it in sticky bar above) */}
              {showQrColumn ? (
                !isMobile ? (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.18, duration: 0.5 }}
                  style={{
                    position: "sticky",
                    top: 24,
                    width: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    overflow: "hidden",
                  }}
                >
                  <div style={{
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                  }}>
                    <QrCode
                      url={qrUrl}
                      stats={qrStats}
                      size={isNarrow ? 160 : 240}
                      lang={lang}
                      theme={theme}
                      compact={isNarrow}
                    />
                  </div>
                </motion.div>
                ) : null
              ) : (
                // Empty cell to keep grid alignment on desktop only
                <div style={{ display: (isMobile || isNarrow) ? "none" : "block" }} />
              )}

              {/* ── Centre: About — DESKTOP ONLY (tablet/mobile rendered above) ── */}
              {!isMobile && !isNarrow && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  style={{
                    background: t.card,
                    border: `1px solid ${t.border}`,
                    borderRadius: 20,
                    padding: "28px 32px",
                    minWidth: 0,
                    overflow: "hidden",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  <SectionLabel t={t}>{lang === "fr" ? "À propos" : "About the event"}</SectionLabel>
                  <p style={{
                    fontSize: "0.93rem", lineHeight: 1.8, color: t.fgMuted,
                    whiteSpace: "pre-wrap", wordBreak: "break-word",
                    overflowWrap: "break-word", minWidth: 0, maxWidth: "100%",
                    textAlign: "center",
                  }}>
                    {event.description || (lang === "fr" ? "Pas de description." : "No description provided.")}
                  </p>
                </motion.div>
              )}

              {/* ── Right: Map + Join ── */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                style={{
                  position: isMobile ? "static" : "sticky",
                  top: 24,
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  minWidth: 0,
                  width: "100%",
                  boxSizing: "border-box",
                  overflow: "hidden",
                }}
              >
                {/* Map card */}
                <div
                  style={{
                    background: t.card,
                    border: `1px solid ${t.border}`,
                    borderRadius: isMobile ? 16 : 20,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: isMobile ? 200 : 260,
                      position: "relative",
                    }}
                  >
                    {event.latitude && event.longitude ? (
                      <Map
                        initialViewState={{
                          latitude: event.latitude,
                          longitude: event.longitude,
                          zoom: isMobile ? 14.5 : 15.2,
                        }}
                        mapStyle="https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json"
                        style={{ width: "100%", height: "100%" }}
                      >
                        <Marker latitude={event.latitude} longitude={event.longitude}>
                          <div
                            style={{
                              width: 16,
                              height: 16,
                              background: t.primary,
                              borderRadius: "50%",
                              border: "3px solid white",
                              boxShadow: `0 0 20px ${t.primary}99`,
                              animation: "pulse-glow 2s ease-in-out infinite",
                              transform: "translate(-50%, -50%)",
                            }}
                          />
                        </Marker>
                      </Map>
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: isDark ? "#0d1424" : t.card,
                          color: t.fgSubtle,
                          fontSize: "0.88rem",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: "1.5rem", opacity: 0.4 }}>🗺️</span>
                        <span>{lang === "fr" ? "Carte non disponible" : "Map not available"}</span>
                      </div>
                    )}
                  </div>

                  {event.location && (
                    <a
                      href={
                        event.latitude && event.longitude
                          ? `https://maps.google.com/?q=${event.latitude},${event.longitude}`
                          : `https://maps.google.com/?q=${encodeURIComponent(event.location)}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: "14px 18px",
                        borderTop: `1px solid ${t.border}`,
                        fontSize: "0.82rem",
                        color: t.fgMuted,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        textDecoration: "none",
                        cursor: "pointer",
                        transition: "background 0.15s, color 0.15s",
                        borderBottomLeftRadius: "inherit",
                        borderBottomRightRadius: "inherit",
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = t.surfaceHover;
                        e.currentTarget.style.color = t.primary;
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = t.fgMuted;
                      }}
                    >
                      <span style={{ flexShrink: 0 }}>📍</span>
                      <span style={{ wordBreak: "break-word", overflowWrap: "break-word", minWidth: 0 }}>{event.location}</span>
                    </a>
                  )}
                </div>

                {/* Action card — on mobile only shows admin actions (price + join are in sticky bar above) */}
                {(!isMobile || event.is_mine || event.is_co_admin) && (
                <div
                  style={{
                    background: t.card,
                    border: `1px solid ${t.border}`,
                    borderRadius: isMobile ? 16 : 20,
                    padding: "24px",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  {/* Price display — desktop only */}
                  {!isMobile && (
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 20 }}>
                    <span
                      style={{
                        fontFamily: "'DM Serif Display', serif",
                        fontSize: "2rem",
                        fontWeight: 400,
                        ...(t.priceColor
                          ? { color: t.priceColor }
                          : {
                              background: `linear-gradient(135deg, ${t.primary}, ${t.accent})`,
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                            }),
                        letterSpacing: "-0.02em",
                        lineHeight: 1,
                      }}
                    >
                      {priceText}
                    </span>
                  </div>
                  )}

                  {!isMobile && <Divider t={t} />}

                  {/* Join button — desktop only */}
                  {!isMobile && (
                  <motion.button
                    onClick={joinEvent}
                    disabled={registrationLoading || !!registration || !event || loading}
                    whileHover={btnState === "join" ? { scale: 1.02 } : {}}
                    whileTap={btnState === "join" ? { scale: 0.98 } : {}}
                    style={{
                      width: "100%",
                      padding: "15px",
                      background: joinBtnBg,
                      color: joinBtnColor,
                      border: "none",
                      borderRadius: 14,
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      cursor: registrationLoading || !!registration ? "default" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      fontFamily: "'DM Sans', sans-serif",
                      letterSpacing: "0.01em",
                      boxShadow: btnState === "join"
                        ? `0 4px 24px rgba(99,102,241,0.32)`
                        : "none",
                      transition: "box-shadow 0.2s",
                    }}
                  >
                    {registrationLoading && <Spinner />}
                    {joinBtnLabel}
                  </motion.button>
                  )}

                  {/* Owner / Admin actions */}
                  {(event.is_mine || event.is_co_admin) && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
                      {event.is_mine && (
                        <button
                          onClick={() => navigate(`/events/${event.id}/edit`)}
                          style={{
                            width: "100%",
                            padding: "13px",
                            background: "transparent",
                            border: `1.5px solid ${t.borderStrong}`,
                            borderRadius: 14,
                            color: t.primary,
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            cursor: "pointer",
                            fontFamily: "'DM Sans', sans-serif",
                            transition: "background 0.2s",
                          }}
                          onMouseEnter={(e) => (e.target.style.background = `${t.primary}10`)}
                          onMouseLeave={(e) => (e.target.style.background = "transparent")}
                        >
                          {lang === "fr" ? "Modifier l'événement" : "Edit Event"}
                        </button>
                      )}

                      <button
                        onClick={() => navigate(`/events/${event.slug || event.id}/admin`)}
                        style={{
                          width: "100%",
                          padding: "13px",
                          background: t.btnGradient,
                          color: t.btnColor,
                          border: "none",
                          borderRadius: 14,
                          fontWeight: 600,
                          fontSize: "0.9rem",
                          cursor: "pointer",
                          fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        {lang === "fr" ? "Panneau d'administration" : "Admin Panel"}
                      </button>
                    </div>
                  )}
                </div>
                )}
              </motion.div>
            </div>

            {/* ── Comments Section ── */}
            <CommentsSection
              t={t} lang={lang} token={token} isMobile={isMobile}
              comments={comments} commentsLoading={commentsLoading}
              commentBody={commentBody} setCommentBody={setCommentBody}
              commentPosting={commentPosting} postComment={postComment}
              replyingTo={replyingTo} setReplyingTo={setReplyingTo}
              replyBody={replyBody} setReplyBody={setReplyBody}
              replyPosting={replyPosting} postReply={postReply}
              deleteComment={deleteComment}
              currentUserId={currentUserId}
              onNeedAuth={() => setIsAuthModalOpen(true)}
            />
          </div>
        </div>

        {/* ── Auth modal ── */}
        {isAuthModalOpen && (
          <AuthModal
            isOpen={isAuthModalOpen}
            onClose={() => setIsAuthModalOpen(false)}
            lang={lang}
            theme={theme}
            translations={{
              en: {
                login: "Sign in", register: "Register", email: "Email", password: "Password",
                name: "Username", enterInviteCode: "Invite code", login_link: "Already have an account? Sign in",
                no_account: "Don't have an account? Register", or_sign_in_with: "or sign in with",
                or_sign_up_with: "or sign up with", emailRequired: "Email is required",
                passwordRequired: "Password is required", inviteCodeRequired: "Invite code required",
                invalidInviteCode: "Invalid invite code", invalidEmail: "Invalid email format",
                error: "Error", oauthError: "OAuth authorization failed", loading: "Loading…",
                eventNotFound: "Event not found", joinEventError: "Failed to join event",
                backToEvents: "Back to Events",
              },
              fr: {
                login: "Se connecter", register: "S'inscrire", email: "Email", password: "Mot de passe",
                name: "Nom d'utilisateur", enterInviteCode: "Code d'invitation",
                login_link: "Vous avez déjà un compte ? Connectez-vous",
                no_account: "Pas de compte ? Inscrivez-vous", or_sign_in_with: "ou connectez-vous avec",
                or_sign_up_with: "ou inscrivez-vous avec", emailRequired: "Email requis",
                passwordRequired: "Mot de passe requis", inviteCodeRequired: "Code d'invitation requis",
                invalidInviteCode: "Code d'invitation invalide", invalidEmail: "Format d'email invalide",
                error: "Erreur", oauthError: "Échec de l'autorisation OAuth", loading: "Chargement…",
                eventNotFound: "Événement non trouvé", joinEventError: "Échec de l'inscription",
                backToEvents: "Retour aux événements",
              },
            }}
            onLogin={handleAuthSuccess}
            eventInviteCode={event?.invite_code}
            initialMode="register"
          />
        )}

        {/* ── Phone modal ── */}
        {showPhoneModal && (
          <PhoneModal
            lang={lang}
            t={t}
            phoneInput={phoneInput}
            setPhoneInput={setPhoneInput}
            phoneError={phoneError}
            setPhoneError={setPhoneError}
            registrationLoading={registrationLoading}
            onSubmit={joinEventWithPhone}
            onClose={() => { setShowPhoneModal(false); setPhoneError(""); }}
            isGuruDomain={isGuruDomain}
          />
        )}
      </div>
    </>
  );
}